/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useGate, useStore } from 'effector-react';
import type { SortEnd } from 'react-sortable-hoc';
import type { Subtitle } from '@types';
import Title from '@components/Title';
import Tip from '@components/Tip';
import { CollapseWrapper } from '@components/Collapse';
import Button from '@components/Button';
import SortableList from '@components/SortableList';
import { $loadedScene } from '@stores/loadedStore';
import { $sceneMapApi } from '@stores/dataStores';
import arrayMove from '@utils/arrayMove';
import { showApplyWarning } from '@components/ConfirmModal';
import { sendSuccess } from '@components/Noti';
import { $GateSubtitleList } from '@stores/editingStores';
import SidebarApplyTo from '../../sidebar/components/SidebarApplyTo';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import { showSceneModal } from '../../category';
import { PanelSubtitle, type PanelSubtitleProps } from '../PanelSubtitle';
import { SidebarSubtitleItem } from './SidebarSubtitleItem';

const MAX = 5;

export function SidebarSubtitle() {
  const { subtitle, id } = useStore($loadedScene)!;
  const list = subtitle ?? [];
  const [panel, setPanel] = useState<{
    index: number;
    defaultValue?: Subtitle;
  } | null>(null);

  useGate($GateSubtitleList, list);

  const handleConfirm: PanelSubtitleProps['onConfirm'] = (index, sub) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.subtitle ??= [];
        draft.subtitle[index] = sub;
      },
    });
  };

  const handleDelete: PanelSubtitleProps['onDeleteClick'] = (index: number) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.subtitle ??= [];
        draft.subtitle.splice(index, 1);
      },
    });
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.subtitle = arrayMove({
          array: draft.subtitle ?? [],
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        });
      },
    });
  };

  const handleApplyTo = async () => {
    if (!list.length) {
      const { confirmed } = await showApplyWarning();
      if (!confirmed) return;
    }
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      multiple: true,
      disabledIds: [id],
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const item = draft.get(_id);
        if (!item) return;
        item.subtitle = list;
      });
    });
    sendSuccess('公告字幕已应用到所选场景');
  };

  return (
    <CollapseWrapper>
      <div className="absolute inset-0 flex flex-col">
        <SidebarHeader
          title={
            <div className="flex">
              <Title>公告字幕</Title>
              <Tip className="ml-10 self-end text-base">
                {`${list.length}/${MAX}`}
              </Tip>
            </div>
          }
        />
        <Button
          size="lg"
          className="mb-10 w-full"
          disabled={list.length >= MAX}
          onClick={() => setPanel({ index: list.length })}
        >
          添加字幕
        </Button>
        {list.length > 0 ? (
          <div className="relative flex-1">
            <SortableList
              axis="y"
              lockAxis="y"
              distance={10}
              onSortEnd={handleSortEnd}
            >
              <Scrollbars>
                {list.map((item, i) => (
                  <SidebarSubtitleItem
                    key={i}
                    index={i}
                    listIndex={i}
                    item={item}
                    onClick={setPanel}
                    onDeleteClick={handleDelete}
                  />
                ))}
              </Scrollbars>
            </SortableList>
          </div>
        ) : (
          <div className="flex-1">
            <Tip className="mt-5 text-center text-base">{`最多可添加${MAX}条字幕`}</Tip>
            <Tip className="mt-5 text-center text-base">
              添加后支持上下拖拽调整字幕顺序
            </Tip>
          </div>
        )}
        <SidebarApplyTo
          className="mt-10"
          title="把公告字幕设置应用到..."
          onClick={handleApplyTo}
        />
      </div>
      {!!panel && (
        <PanelSubtitle
          index={panel.index}
          defaultValue={panel.defaultValue}
          onConfirm={handleConfirm}
          onDeleteClick={handleDelete}
          onRequestClose={() => setPanel(null)}
        />
      )}
    </CollapseWrapper>
  );
}
